import { useState, useEffect } from 'react';
import { ModalDialog, ButtonBar, FlowForm } from 't-lib-ui/structure';
import { TextButton, DropDownSelector, DropDownSelectorValue, Input } from 't-lib-ui/widgets';
import { getAdminMagicSecret, setAdminMagicSecret } from '../../connector/auth';
import { usePutAdminFeed, usePutAdminCatalog } from '../../connector/api';
import { FeedCategoryList } from '../../schema-copy/feed-schema';
import './AdminCategoryPopup.css';

interface AdminCategoryPopupProps {
  show: boolean;
  feedId: string;
  /** True for catalog entries, whose category lives on the catalog row and overrides the feed's. */
  isCatalog: boolean;
  category?: string;
  onClose: () => void;
  /** Called with the new category after a successful save, so the caller can update its display. */
  onChanged: (category: string) => void;
}

/**
 * Small admin-only dialog for changing a feed's category from anywhere it is displayed
 * (e.g. a feed tile), without opening the full admin feed page.
 */
const AdminCategoryPopup: React.FC<AdminCategoryPopupProps> = ({ show, feedId, isCatalog, category, onClose, onChanged }) => {
  const [selected, setSelected] = useState(category ?? '');
  const [secret, setSecret] = useState(getAdminMagicSecret() ?? '');
  // The magic secret is normally already in localStorage from an earlier admin page. Only ask for
  // it when it is missing, or after a request failed (which usually means it is wrong).
  const [askSecret, setAskSecret] = useState(!getAdminMagicSecret());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const magic = secret.trim();
  const putAdminFeed = usePutAdminFeed(feedId, magic);
  const putAdminCatalog = usePutAdminCatalog(feedId, magic);

  // Reset the form whenever the dialog (re)opens.
  useEffect(() => {
    if (!show) return;
    setSelected(category ?? '');
    const stored = getAdminMagicSecret();
    setSecret(stored ?? '');
    setAskSecret(!stored);
    setSaving(false);
    setError(null);
  }, [show, feedId, category]);

  // Includes the current value even if it is no longer in FeedCategoryList, so the dropdown never
  // shows an empty selection for a category that is set.
  const options: DropDownSelectorValue[] = [
    { id: '', text: '(None)', searchTextLc: 'none' },
    ...FeedCategoryList.map(c => ({ id: c, text: c, searchTextLc: c.toLowerCase() })),
    ...(category && !FeedCategoryList.includes(category as any)
      ? [{ id: category, text: category, searchTextLc: category.toLowerCase() }]
      : [])
  ];

  const handleSave = async () => {
    if (saving || !magic) return;
    setSaving(true);
    setError(null);
    try {
      setAdminMagicSecret(magic);
      // A catalog entry carries its own category that overrides the feed's, so both rows have to
      // be written to keep the displayed (catalog) category and the feed record in sync.
      if (isCatalog) await putAdminCatalog({ category: selected });
      await putAdminFeed({ category: selected });
      onChanged(selected);
      onClose();
    } catch (e) {
      console.error('Failed to update category:', e);
      setError(e instanceof Error ? e.message : 'Failed to update the category.');
      setAskSecret(true);
      setSaving(false);
    }
  };

  return (
    <ModalDialog
      show={show}
      title="Change Category"
      closeButton="close"
      onClose={onClose}
      className="adminCategoryPopup"
      minWidth="440px"
    >
      <div style={{ padding: '0 20px 20px 20px' }}>
        <FlowForm>
          <DropDownSelector
            selections={options}
            value={selected}
            onSelect={v => setSelected(v.id)}
            focussable="flow"
            disabled={saving}
          />
          {askSecret && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: 4 }}>Admin secret</div>
              <Input
                type="password"
                value={secret}
                onInput={setSecret}
                placeholder="Admin secret"
                onEnter={handleSave}
                layout="singleCol"
              />
            </div>
          )}
          {error && <p style={{ color: 'var(--error-color)', fontSize: '0.85rem' }}>{error}</p>}
          <ButtonBar className="full-margin-top" align="right">
            <TextButton type="minor" onClick={onClose} label="Cancel" />
            <TextButton
              type="major"
              focussable="sideway"
              onClick={handleSave}
              label="Save"
              busy={saving}
              enabled={magic && !saving ? 'enabled' : 'disabled'}
            />
          </ButtonBar>
        </FlowForm>
      </div>
    </ModalDialog>
  );
};

export default AdminCategoryPopup;
